import { useState } from "react";
import {
  PhoneMockup,
  MockScreenHome,
  MockScreenJobs,
  MockScreenMarketplace,
  MockScreenShop,
  MockScreenWallet,
  MockScreenSOS,
  MockScreenBlood,
  MockScreenProfile,
  MockScreenChat,
} from "@/components/site/PhoneMockup";
import { HOME } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useCallback } from "react";

const screens = [
  { label: "Home", el: <MockScreenHome /> },
  { label: "Jobs", el: <MockScreenJobs /> },
  { label: "Marketplace", el: <MockScreenMarketplace /> },
  { label: "Shops", el: <MockScreenShop /> },
  { label: "Wallet", el: <MockScreenWallet /> },
  { label: "SOS", el: <MockScreenSOS /> },
  { label: "Blood", el: <MockScreenBlood /> },
  { label: "Profile", el: <MockScreenProfile /> },
  { label: "Chat", el: <MockScreenChat /> },
];

export function Screenshots() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="screenshots" className="py-20 md:py-28 bg-[#1A1918] text-white overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-4 py-1.5 text-xs font-semibold border border-white/10">
            Screenshots
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-tight">
            A quick tour of <span className="brand-gradient-text">Time2Cash.</span>
          </h2>
          <p className="mt-4 text-lg text-white/70">
            Real screens, tapped every day by people across Indian cities.
          </p>
        </div>

        <div className="mt-14 relative" data-testid={HOME.screenshotCarousel}>
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-6 md:gap-8">
              {screens.map((s, i) => {
                const isActive = i === selected;
                return (
                  <div
                    key={s.label}
                    className={`min-w-0 shrink-0 grow-0 basis-[70%] sm:basis-[45%] md:basis-[32%] lg:basis-[26%] transition-all duration-500 ${
                      isActive ? "scale-100 opacity-100" : "scale-90 opacity-60"
                    }`}
                  >
                    <PhoneMockup>{s.el}</PhoneMockup>
                    <div className="mt-4 text-center text-sm font-semibold text-white/70">{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex justify-center gap-3">
            <button
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              data-testid={HOME.screenshotPrev}
              aria-label="Previous"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => emblaApi && emblaApi.scrollNext()}
              data-testid={HOME.screenshotNext}
              aria-label="Next"
              className="w-11 h-11 rounded-full bg-[#FF5A00] hover:bg-[#E65100] flex items-center justify-center transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const reviews = [
  {
    name: "Ramesh K.",
    role: "Resident · Anantapur",
    stars: 5,
    text: "Time2Cash really helped me get my household task completed quickly. Finding local help was much easier than I expected.",
  },
  {
    name: "Priya S.",
    role: "Working Professional · Anantapur",
    stars: 5,
    text: "I needed help cleaning and organizing my room. Through Time2Cash, I found someone nearby who helped me the same day.",
  },
  {
    name: "Harsha V.",
    role: "College Student · Anantapur",
    stars: 5,
    text: "During a medical emergency, we posted a blood request and quickly connected with nearby donors. It was a truly helpful feature.",
  },
  {
    name: "Mahesh Reddy",
    role: "Shop Owner · Anantapur",
    stars: 5,
    text: "Time2Cash is helping more local customers discover my shop. It's a great platform to reach people in our city.",
  },
  {
    name: "Lakshmi P.",
    role: "Homeowner · Anantapur",
    stars: 5,
    text: "No more searching through multiple contacts for electricians or plumbers. I found the right person in just a few minutes.",
  },
  {
    name: "Naveen C.",
    role: "Business Owner · Anantapur",
    stars: 5,
    text: "From local services to jobs and marketplace listings, everything I need is available in one app. It saves a lot of time.",
  },
];

export function Testimonials() {
  return (
    <section className="py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
            Testimonials
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
            Loved by cities. <span className="brand-gradient-text">Trusted by neighbours.</span>
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div
              key={r.name}
              className={`rounded-3xl p-7 border ${
                i % 3 === 1 ? "bg-[#1A1918] text-white border-[#1A1918]" : "bg-white border-[#EBE8E3]"
              }`}
            >
              <Quote className={`w-8 h-8 ${i % 3 === 1 ? "text-[#FF5A00]" : "text-[#FF5A00]"}`} />
              <p className={`mt-4 text-lg leading-relaxed ${i % 3 === 1 ? "text-white" : "text-[#1A1918]"}`}>&ldquo;{r.text}&rdquo;</p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <div className={`font-bold ${i % 3 === 1 ? "text-white" : "text-[#1A1918]"}`}>{r.name}</div>
                  <div className={`text-xs ${i % 3 === 1 ? "text-white/60" : "text-[#78716C]"}`}>{r.role}</div>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: r.stars }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#FF5A00] text-[#FF5A00]" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-[#78716C] italic">* Sample reviews — real reviews coming soon.</p>
      </div>
    </section>
  );
}

export function Ambassador() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "", email: "" });

  const onSubmit = (e) => {
  e.preventDefault();

  const message = `*New Time2Cash Ambassador Application*

👤 *Full Name:* ${form.name}
📱 *Phone:* ${form.phone}
🏙️ *City:* ${form.city}
📧 *Email:* ${form.email}

I would like to become a *Time2Cash Ambassador*.`;

  const whatsappUrl = `https://wa.me/919988771606?text=${encodeURIComponent(message)}`;

  window.open(whatsappUrl, "_blank");

  setSubmitted(true);
};

  return (
    <section id="ambassador" className="py-20 md:py-28 bg-[#FFF9F5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
              Ambassador Program
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
              Become a <br />
              <span className="brand-gradient-text">Time2Cash Ambassador.</span>
            </h2>
            <p className="mt-5 text-lg text-[#4A4846] leading-relaxed">
              Help introduce Time2Cash to your city and community. Support local growth
              while representing the platform through events, campaigns, and referrals.
            </p>
            <ul className="mt-6 space-y-2 text-[#4A4846]">
              {["Earn rewards for every onboarded shop", "Represent your city officially", "Access to exclusive campaigns"].map((l) => (
                <li key={l} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00]" /> {l}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-white border border-[#EBE8E3] p-8 shadow-sm">
            {!submitted ? (
              <form onSubmit={onSubmit} data-testid={HOME.ambassadorForm} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-[#78716C]">Full name</label>
                  <input
                    required
                    data-testid={HOME.ambassadorName}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]"
                    placeholder="Your name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[#78716C]">Phone</label>
                    <input
                      required
                      type="tel"
                      data-testid={HOME.ambassadorPhone}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]"
                      placeholder="+91"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#78716C]">City</label>
                    <input
                      required
                      data-testid={HOME.ambassadorCity}
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]"
                      placeholder="Your city"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#78716C]">Email</label>
                  <input
                    required
                    type="email"
                    data-testid={HOME.ambassadorEmail}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#EBE8E3] bg-[#FDFBF7] px-4 py-3 text-sm outline-none focus:border-[#FF5A00]"
                    placeholder="you@email.com"
                  />
                </div>
                <button
                  type="submit"
                  data-testid={HOME.ambassadorSubmit}
                  className="w-full rounded-full bg-[#25D366] hover:bg-[#1EBE5B] text-white py-3.5 font-semibold shadow-lg shadow-green-500/30 transition-colors"
                >
                  Apply via WhatsApp
                </button>
              </form>
            ) : (
              <div data-testid={HOME.ambassadorSuccess} className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                  <Star className="w-7 h-7 text-emerald-600 fill-emerald-600" />
                </div>
                <h3 className="mt-4 text-2xl font-bold text-[#1A1918]">Application received!</h3>
                <p className="mt-2 text-[#4A4846]">
                  Thanks for applying. Our team will reach out to you shortly on the email and phone you provided.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
